import { Link } from 'react-router-dom';
import { 
  Shield, 
  CheckCircle, 
  Settings, 
  Truck, 
  FileCheck, 
  Award,
  Wrench,
  Eye,
  ClipboardCheck,
  Package,
  ArrowRight,
  Phone
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const processSteps = [
  {
    step: '01',
    icon: Eye,
    title: 'Inspection & Assessment',
    description: 'Every engine undergoes a comprehensive visual and mechanical inspection by our certified technicians. We document condition, hours, and any wear patterns.',
    details: ['Complete visual inspection', 'Hours verification', 'Leak detection', 'Component assessment']
  },
  {
    step: '02',
    icon: ClipboardCheck,
    title: 'Testing & Diagnostics',
    description: 'Using state-of-the-art diagnostic equipment, we run full performance tests to ensure each engine meets manufacturer specifications.',
    details: ['Compression testing', 'Oil pressure analysis', 'Electronic diagnostics', 'Load testing when applicable']
  },
  {
    step: '03',
    icon: Wrench,
    title: 'Reconditioning',
    description: 'Engines requiring work are reconditioned by our expert mechanics using OEM or equivalent parts, restoring them to optimal operating condition.',
    details: ['OEM parts replacement', 'Seal & gasket renewal', 'Fluid system service', 'Calibration & tuning']
  },
  {
    step: '04',
    icon: FileCheck,
    title: 'Documentation',
    description: 'Complete documentation package including inspection reports, test results, service history, and certification of condition.',
    details: ['Detailed inspection report', 'Test result certificates', 'Service records', 'Photo documentation']
  },
  {
    step: '05',
    icon: Package,
    title: 'Preparation & Packaging',
    description: 'Engines are properly preserved, mounted on custom skids, and packaged for safe transport to anywhere in the world.',
    details: ['Preservation treatment', 'Custom mounting', 'Weather protection', 'Secure packaging']
  },
  {
    step: '06',
    icon: Truck,
    title: 'Delivery & Support',
    description: 'We coordinate worldwide shipping and provide ongoing technical support to ensure your complete satisfaction.',
    details: ['Global shipping coordination', 'Customs documentation', 'Installation guidance', 'Technical support']
  }
];

const guarantees = [
  {
    icon: Shield,
    title: '30-Day Warranty',
    description: 'All rebuilt and reconditioned engines come with a minimum 30-day warranty covering major components and workmanship.'
  },
  {
    icon: CheckCircle,
    title: 'Accurate Descriptions',
    description: 'Every listing includes honest, detailed condition reports. What you see is exactly what you get - no surprises.'
  },
  {
    icon: Award,
    title: 'Quality Certified',
    description: 'Our facility and processes are certified to international quality standards, ensuring consistent excellence.'
  },
  {
    icon: Settings,
    title: 'Technical Support',
    description: 'Our engineering team provides pre and post-sale technical support to help with selection, installation, and operation.'
  }
];

export default function ProcessesPage() {
  return (
    <main className="bg-slate-50">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-orange-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/20 rounded-full text-orange-400 text-sm font-medium mb-6">
              <Shield className="w-4 h-4" />
              Quality You Can Trust
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Our Process &
              <span className="text-orange-500"> Guarantee</span>
            </h1>
            <p className="text-xl text-slate-300 mb-8">
              At EngineShipParts, we&apos;ve developed a rigorous process to ensure every marine engine 
              we sell meets the highest standards of quality, reliability, and value.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/inventory">
                <Button className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3">
                  Browse Inventory
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 px-6 py-3">
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Our 6-Step Quality Process
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Every engine passes through our comprehensive quality assurance process before being listed for sale.
            </p>
          </div>

          <div className="grid gap-8">
            {processSteps.map((step, index) => (
              <div 
                key={step.step}
                className={`flex flex-col lg:flex-row gap-8 items-start ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                {/* Step Card */}
                <div className="flex-1 bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-slate-100">
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center shadow-lg">
                        <step.icon className="w-8 h-8 text-white" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-5xl font-bold text-slate-200">{step.step}</span>
                        <h3 className="text-2xl font-bold text-slate-900">{step.title}</h3>
                      </div>
                      <p className="text-slate-600 mb-4">{step.description}</p>
                      <ul className="grid grid-cols-2 gap-2">
                        {step.details.map((detail, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm text-slate-500">
                            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                            {detail}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Connector Line (desktop) */}
                {index < processSteps.length - 1 && (
                  <div className="hidden lg:flex items-center justify-center w-16">
                    <div className="w-1 h-full bg-gradient-to-b from-orange-500 to-red-500 rounded-full min-h-[100px]"></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Guarantees */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Our Guarantee To You
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We stand behind every engine we sell with comprehensive guarantees and ongoing support.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {guarantees.map((guarantee, index) => (
              <div 
                key={index}
                className="group bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-8 hover:from-orange-50 hover:to-orange-100 transition-all duration-300 border border-slate-200 hover:border-orange-300"
              >
                <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mb-6 shadow-md group-hover:shadow-lg transition-shadow">
                  <guarantee.icon className="w-7 h-7 text-orange-500" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{guarantee.title}</h3>
                <p className="text-slate-600">{guarantee.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-500 to-red-500">
        <div className="container-custom text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Ready to Find Your Perfect Engine?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Browse our inventory of quality-assured marine engines or speak with our team to find exactly what you need.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/inventory">
              <Button className="bg-white text-orange-600 hover:bg-slate-100 px-8 py-3 text-lg font-semibold">
                Browse Inventory
              </Button>
            </Link>
            <a href="tel:+12012344851">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-3 text-lg font-semibold">
                <Phone className="w-5 h-5 mr-2" />
                Call Us Now
              </Button>
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
